import React, { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import {
    FullInput,
    Icon,
    styled
  } from "../../../../seenspire-library";
  import { getToken } from "../../../backend/session";
  import { api } from "../../../backend/api";
  import {
    CustomChannelSourceTypeEnum as TypesEnum,
  } from "../../../interfaces";

const getHeaders = () => {
    const currentToken = getToken();
  
    const headers = {
      Authorization: `Bearer ${currentToken.access_token}`,
    };
  
    return headers;
  };
  
const SelectIcon = styled(Icon)`
  width: 24px;
  height: 24px;
`;

interface SharePointSiteFieldsProps {
  connection: any;
  selectedType: any;
  singleInputCustomStyles: any;
  onValidationChange: (isValid: boolean) => void;
  onFieldsChange: (fields: { host_name: string; site_name: string; site_id?: string; display_name?: string }) => void;
  onError: (error: { error: boolean; message: string }) => void;
  onValidatingChange: (isValidating: boolean) => void;
}

export const SharePointSiteFields: React.FC<SharePointSiteFieldsProps> = ({
  connection,
  selectedType,
  singleInputCustomStyles,
  onValidationChange,
  onFieldsChange,
  onError,
  onValidatingChange,
}) => {
  const [hostNameInput, setHostNameInput] = useState("");
  const [siteNameInput, setSiteNameInput] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateSharePointSite = async (hostName: string, siteName: string) => {
    if (!hostName || !siteName || !connection?.id) {
      console.log("Missing required data:", { hostName, siteName, connectionId: connection?.id });
      return;
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setIsValidating(true);
    
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API}/api/networkconnections/${connection.id}/customoptions/${TypesEnum.SHAREPOINTSITE}/options/site_id`,
        {
          method: "GET",
          headers: getHeaders(),
          params: {
            host_name: hostName,
            site_name: siteName
          },
          signal: abortController.signal
        }
      );

      if (!abortController.signal.aborted) {

        if (Array.isArray(response.data)) {
          onValidationChange(false);
          onError({ error: true, message: "No sites found. Please check your host name and site name." });
          return;
        }

        if (response.data) {
          onFieldsChange({
            host_name: hostName,
            site_name: siteName,
            site_id: response.data.id,
            display_name: response.data.displayName
          });
          onError({ error: false, message: "" });
          onValidationChange(true);
        }
      }
    } catch (error: any) {
      if (!abortController.signal.aborted) {
        onValidationChange(false);
        
        if (error.name === 'AbortError') {
          console.log("Request was aborted");
          return;
        }
        
        if (error.response) {
          if (error.response.status === 500) {
            onError({
              error: true,
              message: error.response.data?.error?.message ?? "Server error occurred. Please try again later."
            });
          } else {
            onError({
              error: true,
              message: error.response.data?.message || "Invalid SharePoint site details. Please check your host name and site name."
            });
          }
        } else if (error.request) {
          onError({
            error: true,
            message: "Unable to connect to server. Please check your connection."
          });
        } else {
          onError({
            error: true,
            message: "An unexpected error occurred. Please try again."
          });
        }
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsValidating(false);
      }
    }
  };

  const handleInputChange = (value: string, isHostName: boolean) => {
    setIsValidating(true);
    onValidatingChange(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (isHostName) {
      setHostNameInput(value);
      onFieldsChange({
        host_name: value,
        site_name: siteNameInput
      });
      if (value && siteNameInput) {
        debouncedValidation(value, siteNameInput);
      }
    } else {
      setSiteNameInput(value);
      onFieldsChange({
        host_name: hostNameInput,
        site_name: value
      });
      if (value && hostNameInput) {
        debouncedValidation(hostNameInput, value);
      }
    }
  };

  const debouncedValidation = useCallback(
    debounce((hostName: string, siteName: string) => {
      if (!connection?.id) {
        console.log("No connection available for validation");
        setIsValidating(false);
        onValidatingChange(false);
        return;
      }
      validateSharePointSite(hostName, siteName);
    }, 1000),
    [connection?.id]
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      debouncedValidation.cancel();
    };
  }, [debouncedValidation]);

  useEffect(() => {
    if (connection?.id && hostNameInput && siteNameInput) {
      console.log("Connection changed, triggering validation");
      debouncedValidation(hostNameInput, siteNameInput);
    }
  }, [connection]);

  useEffect(() => {
    onValidatingChange(isValidating);
  }, [isValidating, onValidatingChange]);

  return (
    <>
      <FullInput
        customStyles={singleInputCustomStyles}
        icon={<SelectIcon color="gray" name={selectedType.icon} />}
        title="Host Name"
        value={hostNameInput}
        onChange={({ target: { value } }) => handleInputChange(value, true)}
      />
      <FullInput
        customStyles={singleInputCustomStyles}
        icon={<SelectIcon color="gray" name={selectedType.icon} />}
        title="Site Name"
        value={siteNameInput}
        onChange={({ target: { value } }) => handleInputChange(value, false)}
      />
    </>
  );
};
